import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { supabase } from '../lib/supabase';
import PageHeader from '../components/PageHeader';
import { useConfirm } from '../components/ConfirmDialog';

// إدارة التبرعات: أوجه التبرع كمجموعات + اختياراتها الفرعية.
// كل صف فيه اسم ووصف بس — والمشرف يحدد الأب لو الوجه ده اختيار فرعي
// تحت وجه رئيسي (مثلاً «علاج مرضى» تحت «زكاة مال»).

type Row = {
  id: string;
  title_ar: string;
  title_en: string | null;
  description_ar: string;
  description_en: string | null;
  parent_id: string | null;
  sort_order: number;
  active: boolean;
};

type Draft = {
  id?: string;
  title_ar: string;
  title_en: string;
  description_ar: string;
  description_en: string;
  parent_id: string | null;
  active: boolean;
};

function generateCauseId(): string {
  return 'c' + crypto.randomUUID().replace(/-/g, '').slice(0, 10);
}

const emptyDraft: Draft = {
  title_ar: '',
  title_en: '',
  description_ar: '',
  description_en: '',
  parent_id: null,
  active: true,
};

export default function CampaignsPage() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Draft | null>(null);
  const [creating, setCreating] = useState(false);
  const confirm = useConfirm();

  useEffect(() => { void load(); }, []);

  const load = async () => {
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from('campaigns')
      .select('id, title_ar, title_en, description_ar, description_en, parent_id, sort_order, active')
      .order('sort_order');
    if (err) setError(err.message);
    else setRows((data ?? []) as Row[]);
    setLoading(false);
  };

  const parents = useMemo(() => rows.filter((r) => !r.parent_id), [rows]);
  const childrenOf = (pid: string) => rows.filter((r) => r.parent_id === pid);

  const openCreate = (parentId: string | null = null) => {
    setCreating(true);
    setEditing({ ...emptyDraft, parent_id: parentId });
  };

  const openEdit = (r: Row) => {
    setCreating(false);
    setEditing({
      id: r.id,
      title_ar: r.title_ar,
      title_en: r.title_en ?? '',
      description_ar: r.description_ar,
      description_en: r.description_en ?? '',
      parent_id: r.parent_id,
      active: r.active,
    });
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    setError(null);
    const sort = editing.parent_id
      ? rows.filter((r) => r.parent_id === editing.parent_id).length
      : parents.length;
    const payload = {
      title_ar: editing.title_ar.trim(),
      title_en: editing.title_en.trim() || null,
      description_ar: editing.description_ar.trim(),
      description_en: editing.description_en.trim() || null,
      parent_id: editing.parent_id,
      active: editing.active,
    };
    const { error: err } = creating
      ? await supabase.from('campaigns').insert({
          id: generateCauseId(),
          ...payload,
          sort_order: sort,
        })
      : await supabase.from('campaigns').update(payload).eq('id', editing.id!);
    if (err) return setError(err.message);
    setEditing(null); setCreating(false); await load();
  };

  const remove = async (r: Row) => {
    const kids = childrenOf(r.id);
    if (!(await confirm({
      title: kids.length > 0
        ? `احذف "${r.title_ar}" و${kids.length} اختيار تابع؟`
        : `احذف "${r.title_ar}"؟`,
      confirmText: 'احذف',
      tone: 'danger',
    }))) return;
    if (kids.length > 0) {
      await supabase.from('campaigns').delete().in('id', kids.map((k) => k.id));
    }
    const { error: err } = await supabase.from('campaigns').delete().eq('id', r.id);
    if (err) setError(err.message); else await load();
  };

  return (
    <>
      <PageHeader
        eyebrow="إدارة التبرعات"
        title="أوجه التبرع"
        subtitle="اسم ووصف لكل وجه تبرع. ممكن كمان تضيف اختيارات فرعيه تحت أي وجه."
        actions={<button className="btn" onClick={() => openCreate(null)}>+ إضافة وجه تبرع</button>}
      />
      {error && <div className="error-inline">{error}</div>}

      {loading ? (
        <div className="empty">جاري التحميل...</div>
      ) : parents.length === 0 ? (
        <div className="card empty">مفيش أوجه تبرع لسه — ابدأ بإضافة وجه.</div>
      ) : (
        parents.map((p) => {
          const kids = childrenOf(p.id);
          return (
            <div key={p.id} className="card" style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <strong style={{ fontSize: 16 }}>{p.title_ar}</strong>
                  {p.description_ar && (
                    <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>{p.description_ar}</div>
                  )}
                </div>
                {!p.active && <span className="badge badge-muted">مخفي</span>}
                <button className="btn btn-small btn-secondary" onClick={() => openEdit(p)}>تعديل</button>
                <button className="btn btn-small btn-danger" onClick={() => void remove(p)}>حذف</button>
                <button className="btn btn-small" onClick={() => openCreate(p.id)}>+ اختيار فرعي</button>
              </div>
              {kids.length > 0 && (
                <table className="table" style={{ marginTop: 12 }}>
                  <thead>
                    <tr>
                      <th>الاسم</th>
                      <th>الوصف</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {kids.map((k) => (
                      <tr key={k.id}>
                        <td>
                          {k.title_ar}
                          {!k.active && <span className="badge badge-muted" style={{ marginInlineStart: 8 }}>مخفي</span>}
                        </td>
                        <td style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{k.description_ar || '—'}</td>
                        <td style={{ display: 'flex', gap: 6 }}>
                          <button className="btn btn-small btn-secondary" onClick={() => openEdit(k)}>تعديل</button>
                          <button className="btn btn-small btn-danger" onClick={() => void remove(k)}>حذف</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          );
        })
      )}

      {editing && (
        <div className="modal-backdrop" onClick={() => setEditing(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {creating
                  ? editing.parent_id
                    ? `اختيار فرعي جديد تحت "${parents.find((p) => p.id === editing.parent_id)?.title_ar ?? ''}"`
                    : 'وجه تبرع جديد'
                  : `تعديل: ${editing.title_ar}`}
              </h2>
              <button className="btn btn-small btn-secondary" onClick={() => setEditing(null)}>إغلاق</button>
            </div>
            <form onSubmit={save}>
              <div className="row">
                <div className="field">
                  <label>الاسم (عربي)</label>
                  <input
                    required
                    value={editing.title_ar}
                    onChange={(e) => setEditing({ ...editing, title_ar: e.target.value })}
                    placeholder="مثلاً: تبرع عام"
                  />
                </div>
                <div className="field">
                  <label>Name (English)</label>
                  <input
                    value={editing.title_en}
                    onChange={(e) => setEditing({ ...editing, title_en: e.target.value })}
                    placeholder="e.g. General donation"
                  />
                </div>
              </div>
              <div className="row">
                <div className="field">
                  <label>الوصف (عربي)</label>
                  <textarea
                    rows={3}
                    value={editing.description_ar}
                    onChange={(e) => setEditing({ ...editing, description_ar: e.target.value })}
                    placeholder="وصف مختصر يظهر تحت الاسم"
                  />
                </div>
                <div className="field">
                  <label>Description (English)</label>
                  <textarea
                    rows={3}
                    value={editing.description_en}
                    onChange={(e) => setEditing({ ...editing, description_en: e.target.value })}
                    placeholder="Short description shown under the name"
                  />
                </div>
              </div>
              <div className="field">
                <label>الحالة</label>
                <select value={editing.active ? 'y' : 'n'} onChange={(e) => setEditing({ ...editing, active: e.target.value === 'y' })}>
                  <option value="y">ظاهر</option>
                  <option value="n">مخفي</option>
                </select>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setEditing(null)}>إلغاء</button>
                <button type="submit" className="btn">{creating ? 'نشر' : 'حفظ'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
